import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { Sparkles, CheckCircle2, XCircle, Info, Zap, ChevronDown, ChevronUp, AlertCircle } from 'lucide-react';
import type { EMCode, EMDecomposedMeta, PatientTypeDecisionDto } from '../../types/codingAssistant';
import {
  generateEMCode,
  getEMCode,
  reviewEMCode,
  selectEMVariant,
  manualEntryEMCode
} from '../../services/codingAssistant';
import { isSubscriptionError } from '../../services/api';
import { EMCodeAmbiguousChoice } from './EMCodeAmbiguousChoice';
import { EMCodeCatalogPicker } from './EMCodeCatalogPicker';
import { useCodingAssistantStore } from '../../stores/codingAssistantStore';

interface Props {
  appointmentId?: string;
  encounterId?: string;
  isEncounterMode?: boolean;
}

export const EMCodeSection: React.FC<Props> = ({ appointmentId, encounterId, isEncounterMode = false }) => {
  const [emCode, setEmCode] = useState<EMCode | null>(null);
  const [decomposedMeta, setDecomposedMeta] = useState<EMDecomposedMeta | null>(null);
  const [suppressionReason, setSuppressionReason] = useState<string | null>(null);
  const [emVariants, setEmVariants] = useState<[EMCode, EMCode] | null>(null);
  const [ambiguityReason, setAmbiguityReason] = useState<string | null>(null);
  const [patientTypeDecision, setPatientTypeDecision] = useState<PatientTypeDecisionDto | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [subscriptionBlocked, setSubscriptionBlocked] = useState(false);
  const [showCatalog, setShowCatalog] = useState(false);

  // Publish "E&M variants pending selection" status so the sign-note flow can
  // block signing until the physician resolves the ambiguity.
  const setEmVariantsPending = useCodingAssistantStore((s) => s.setEmVariantsPending);
  const resourceId = appointmentId || encounterId;

  useEffect(() => {
    if (!resourceId) return;
    setEmVariantsPending(resourceId, Boolean(emVariants));
    // Clear the flag on unmount to avoid stale state if the user navigates away.
    return () => {
      setEmVariantsPending(resourceId, false);
    };
  }, [resourceId, emVariants, setEmVariantsPending]);

  const loadEMCode = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await getEMCode(appointmentId, encounterId, isEncounterMode);
      setEmCode(response.data.emCode);
      // GET endpoint returns only persisted data — no decomposedMeta or suppressionReason
      setDecomposedMeta(null);
      setSuppressionReason(null);
    } catch (error) {
      if (isSubscriptionError(error)) {
        setSubscriptionBlocked(true);
      }
      setEmCode(null);
    } finally {
      setIsLoading(false);
    }
  }, [appointmentId, encounterId, isEncounterMode]);

  useEffect(() => {
    loadEMCode();
  }, [loadEMCode]);

  const handleGenerate = async () => {
    try {
      setIsGenerating(true);
      const response = await generateEMCode(appointmentId, encounterId, isEncounterMode);

      // Suppressed branch: backend decided documentation is insufficient.
      if (response.data.suggestionSuppressed || (!response.data.emCode && !response.data.emVariants)) {
        setEmCode(null);
        setEmVariants(null);
        setAmbiguityReason(null);
        setPatientTypeDecision(null);
        setDecomposedMeta(null);
        setSuppressionReason(
          response.data.suppressionReason ||
            'E&M code requires manual coding — documentation is insufficient for an automated suggestion.',
        );
        toast(
          'E&M suggestion withheld — please complete documentation and try again.',
          { icon: 'ℹ️' },
        );
        return;
      }

      // Ambiguous branch — two variants for physician to choose from
      if (response.data.emVariants) {
        setEmVariants(response.data.emVariants);
        setAmbiguityReason(response.data.ambiguityReason ?? null);
        setPatientTypeDecision(response.data.patientTypeDecision ?? null);
        setEmCode(null);
        setDecomposedMeta(null);
        setSuppressionReason(null);
        toast('Two E&M options generated — please select patient type.', { icon: 'ℹ️' });
        return;
      }

      // Clear single code
      setEmCode(response.data.emCode ?? null);
      setDecomposedMeta(response.data.decomposedMeta ?? null);
      setEmVariants(null);
      setAmbiguityReason(null);
      setPatientTypeDecision(response.data.patientTypeDecision ?? null);
      setSuppressionReason(null);
      if (response.data.emCode) {
        const c = response.data.emCode;
        const parts = [
          c.patientType ? `${c.patientType} patient` : null,
          c.mdmLevel ? `${c.mdmLevel} MDM` : null,
        ].filter(Boolean);
        const suffix = parts.length ? ` (${parts.join(', ')})` : '';
        toast.success(`E&M code ${c.code} selected${suffix}`);
      }
    } catch (error: any) {
      if (isSubscriptionError(error)) {
        setSubscriptionBlocked(true);
        return;
      }
      const errorMsg = error?.response?.data?.error || 'Failed to generate E&M code';
      toast.error(errorMsg);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleApprove = async () => {
    if (!emCode) return;

    try {
      await reviewEMCode(appointmentId, encounterId, isEncounterMode, 'approve');

      // Update local state
      setEmCode(prev => prev ? { ...prev, status: 'approved' } : null);

      toast.success(`E&M code ${emCode.code} approved`);
    } catch (error: any) {
      if (!isSubscriptionError(error)) {
        const errorMsg = error?.response?.data?.error || 'Failed to approve code';
        toast.error(errorMsg);
      }
    }
  };

  const handleReject = async (notes: string) => {
    if (!emCode) return;

    try {
      await reviewEMCode(appointmentId, encounterId, isEncounterMode, 'reject', notes);

      // Update local state
      setEmCode(prev => prev ? { ...prev, status: 'rejected', reviewNotes: notes } : null);

      toast.success(`E&M code ${emCode.code} rejected`);
    } catch (error: any) {
      if (!isSubscriptionError(error)) {
        const errorMsg = error?.response?.data?.error || 'Failed to reject code';
        toast.error(errorMsg);
      }
    }
  };

  const handleSelectVariant = async (patientType: 'new' | 'established') => {
    try {
      setIsGenerating(true);
      const response = await selectEMVariant(appointmentId, encounterId, isEncounterMode, patientType);
      setEmCode(response.data.emCode);
      setEmVariants(null);
      setAmbiguityReason(null);
      toast.success(`E&M code ${response.data.emCode.code} selected (${patientType} patient)`);
    } catch (error: any) {
      if (!isSubscriptionError(error)) {
        toast.error(error?.response?.data?.error || 'Failed to select variant');
      }
    } finally {
      setIsGenerating(false);
    }
  };

  const handleManualEntry = async (code: string) => {
    try {
      setIsGenerating(true);
      const response = await manualEntryEMCode(appointmentId, encounterId, isEncounterMode, code);
      setEmCode(response.data.emCode);
      setEmVariants(null);
      setAmbiguityReason(null);
      setSuppressionReason(null);
      setDecomposedMeta(null);
      toast.success(`E&M code ${code} set manually`);
    } catch (error: any) {
      if (!isSubscriptionError(error)) {
        toast.error(error?.response?.data?.error || 'Failed to save manual entry');
      }
    } finally {
      setIsGenerating(false);
    }
  };

  if (subscriptionBlocked) {
    return (
      <div className="text-center py-6 text-amber-700 bg-amber-50 border border-amber-200 rounded-lg text-sm">
        <Info className="w-6 h-6 mx-auto mb-2" />
        <p className="font-medium">Subscription required</p>
        <p className="text-xs mt-1 text-amber-600">An active subscription is needed to generate E&M codes.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Catalog picker — takes precedence over every other state */}
      {showCatalog && (
        <EMCodeCatalogPicker
          onPick={async (code) => {
            await handleManualEntry(code);
            setShowCatalog(false);
          }}
          onCancel={() => setShowCatalog(false)}
          isBusy={isGenerating}
        />
      )}

      {/* Full-card spinner during generation/select-variant/manual-entry */}
      {!showCatalog && isGenerating && (
        <div className="border border-gray-200 bg-white rounded-lg p-6 flex items-center justify-center">
          <div className="flex flex-col items-center gap-3">
            <div className="w-8 h-8 border-2 border-purple-600 border-t-transparent rounded-full animate-spin" />
            <p className="text-sm text-gray-600">Selecting best E&M match…</p>
            <p className="text-xs text-gray-500">Analyzing transcript and clinical documentation</p>
          </div>
        </div>
      )}

      {/* Generate Button */}
      {!showCatalog && !isGenerating && !emCode && !emVariants && (
        <button
          onClick={handleGenerate}
          disabled={isGenerating}
          className="w-full flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium text-white bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg"
        >
          <Sparkles className="w-4 h-4" />
          <span>Suggest E&M Code</span>
        </button>
      )}

      {/* Loading state */}
      {!showCatalog && isLoading && (
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
        </div>
      )}

      {/* Ambiguous variants — two cards for physician to choose from */}
      {!showCatalog && !isGenerating && !isLoading && emVariants && (
        <>
          <EMCodeAmbiguousChoice
            variants={emVariants}
            ambiguityReason={ambiguityReason}
            patientTypeDecision={patientTypeDecision}
            onSelect={handleSelectVariant}
            onRegenerate={handleGenerate}
            isBusy={isGenerating}
          />
          <div className="flex justify-center">
            <button
              onClick={() => setShowCatalog(true)}
              className="text-xs text-gray-500 hover:text-gray-700 underline"
            >
              Pick a different code manually
            </button>
          </div>
        </>
      )}

      {/* Suppressed state — decomposed pipeline chose not to suggest */}
      {!showCatalog && !isGenerating && !isLoading && !emCode && !emVariants && suppressionReason && (
        <div className="border border-amber-200 bg-amber-50 rounded-lg p-4 text-sm">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="font-medium text-amber-900">Manual coding required</p>
              <p className="text-xs text-amber-800 mt-1">{suppressionReason}</p>
              <p className="text-xs text-amber-700 mt-2">
                Add more documentation (problems, data reviewed, risk, or total time) and click Generate again.
              </p>
              <button
                onClick={() => setShowCatalog(true)}
                className="mt-2 px-3 py-1.5 text-xs font-medium text-amber-900 bg-white border border-amber-300 rounded-md hover:bg-amber-100"
              >
                Enter code manually
              </button>
            </div>
          </div>
        </div>
      )}

      {/* No code yet (and not suppressed, not ambiguous) */}
      {!showCatalog && !isGenerating && !isLoading && !emCode && !emVariants && !suppressionReason && (
        <div className="text-center py-8 text-gray-500 text-sm">
          <Info className="w-8 h-8 mx-auto mb-2 opacity-50" />
          <p>No E&M code suggested yet.</p>
          <p className="text-xs mt-1">Click the button above to get a suggestion.</p>
        </div>
      )}

      {/* E&M Code Card */}
      {!showCatalog && !isGenerating && emCode && !emVariants && (
        <>
          <EMCodeCard
            emCode={emCode}
            decomposedMeta={decomposedMeta}
            onApprove={handleApprove}
            onReject={handleReject}
            onRegenerate={handleGenerate}
            isRegenerating={isGenerating}
          />
          <div className="flex justify-center">
            <button
              onClick={() => setShowCatalog(true)}
              className="text-xs text-gray-500 hover:text-gray-700 underline"
            >
              Pick a different code manually
            </button>
          </div>
        </>
      )}
    </div>
  );
};

// Individual E&M Code card component
interface EMCodeCardProps {
  emCode: EMCode;
  decomposedMeta?: EMDecomposedMeta | null;
  onApprove: () => void;
  onReject: (notes: string) => void;
  onRegenerate: () => void;
  isRegenerating: boolean;
}

function formatSelectionBasis(basis: EMDecomposedMeta['selectionBasis']): string {
  switch (basis) {
    case 'mdm':
      return 'based on MDM (no time documented)';
    case 'time':
      return 'based on documented time (no MDM evidence)';
    case 'both_agree':
      return 'MDM and time both support this level';
    case 'mdm_higher':
      return 'based on MDM (higher than time-based level)';
    case 'time_higher':
      return 'based on documented time (higher than MDM level)';
    default:
      return basis;
  }
}

const EMCodeCard: React.FC<EMCodeCardProps> = ({
  emCode,
  decomposedMeta,
  onApprove,
  onReject,
  onRegenerate,
  isRegenerating
}) => {
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectNotes, setRejectNotes] = useState('');
  const [isExpanded, setIsExpanded] = useState(false);
  const [descExpanded, setDescExpanded] = useState(false);

  const getStatusStyles = () => {
    switch (emCode.status) {
      case 'approved':
        return 'border-green-300 bg-green-50/50';
      case 'rejected':
        return 'border-red-300 bg-red-50/50 opacity-60';
      default:
        return 'border-gray-200 bg-white hover:border-gray-300';
    }
  };

  const getConfidenceColor = (conf: number) => {
    if (conf >= 0.8) return 'text-green-700 bg-green-50';
    if (conf >= 0.6) return 'text-yellow-700 bg-yellow-50';
    return 'text-red-700 bg-red-50';
  };

  const getMDMLevelColor = () => {
    switch (emCode.mdmLevel) {
      case 'high':
        return 'bg-red-100 text-red-800';
      case 'moderate':
        return 'bg-orange-100 text-orange-800';
      case 'low':
        return 'bg-yellow-100 text-yellow-800';
      case 'straightforward':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const handleRejectSubmit = () => {
    if (rejectNotes.trim()) {
      onReject(rejectNotes);
      setShowRejectModal(false);
      setRejectNotes('');
    } else {
      toast.error('Please provide a reason for rejection');
    }
  };

  const isPending = emCode.status === 'suggested';

  return (
    <>
      <motion.div
        layout
        initial={{ opacity: 0, y: 5 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, x: -20 }}
        className={`border rounded-lg p-3 transition-all ${getStatusStyles()}`}
      >
        {/* Main Row */}
        <div className="flex items-start gap-3">
          {/* Code Info */}
          <div className="flex-1 min-w-0">
            {/* Code Header Row */}
            <div className="flex items-center gap-1.5 flex-wrap">
              {/* Code */}
              <span className="font-mono font-bold text-base text-gray-900">
                {emCode.code}
              </span>

              {/* MDM Level Badge (omit when mdmLevel is null — e.g., preventive codes) */}
              {emCode.mdmLevel && (
                <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${getMDMLevelColor()}`}>
                  {emCode.mdmLevel.toUpperCase()} MDM
                </span>
              )}

              {/* Patient Type Badge */}
              {emCode.patientType && (
                <span className="px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600 capitalize">
                  {emCode.patientType}
                </span>
              )}

              {/* Confidence */}
              <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${getConfidenceColor(emCode.confidence)}`}>
                {Math.round(emCode.confidence * 100)}%
              </span>

              {/* Status Badge for approved/rejected */}
              {emCode.status === 'approved' && (
                <span className="flex items-center gap-0.5 px-1.5 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">
                  <CheckCircle2 className="w-3 h-3" />
                  Approved
                </span>
              )}
              {emCode.status === 'rejected' && (
                <span className="flex items-center gap-0.5 px-1.5 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700">
                  <XCircle className="w-3 h-3" />
                  Rejected
                </span>
              )}
            </div>

            {/* Description - truncated to 2 lines by default, expandable */}
            <p
              className={descExpanded ? 'text-sm text-gray-700 mt-1' : 'text-sm text-gray-700 mt-1 line-clamp-2'}
              title={emCode.description}
            >
              {emCode.description}
            </p>
            {emCode.description.length > 80 && (
              <button
                onClick={() => setDescExpanded(!descExpanded)}
                className="text-xs text-purple-700 hover:text-purple-900 mt-0.5"
              >
                {descExpanded ? 'See less' : 'See more'}
              </button>
            )}

            {/* Expandable Reasoning */}
            {emCode.reasoning && emCode.reasoning.length > 0 && (
              <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="flex items-center gap-1 mt-1.5 text-xs text-blue-600 hover:text-blue-800 font-medium"
              >
                {isExpanded ? (
                  <>
                    <ChevronUp className="w-3 h-3" />
                    Hide reasoning
                  </>
                ) : (
                  <>
                    <ChevronDown className="w-3 h-3" />
                    Show reasoning ({emCode.reasoning.length} factors)
                  </>
                )}
              </button>
            )}

            {/* Expanded Content */}
            {isExpanded && emCode.reasoning && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="overflow-hidden"
              >
                <div className="mt-2 pl-3 border-l-2 border-blue-300 py-1 space-y-1">
                  {decomposedMeta && (
                    <p className="text-xs font-medium text-blue-700">
                      ▸ {emCode.code} {formatSelectionBasis(decomposedMeta.selectionBasis)}
                    </p>
                  )}
                  {emCode.reasoning.map((reason, idx) => (
                    <p key={idx} className="text-xs text-gray-600">
                      • {reason}
                    </p>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Review Notes (if rejected) */}
            {emCode.status === 'rejected' && emCode.reviewNotes && (
              <p className="text-xs text-red-600 mt-2 italic">
                Rejection reason: {emCode.reviewNotes}
              </p>
            )}
          </div>

          {/* Action Buttons */}
          {isPending && (
            <div className="flex gap-1.5 shrink-0">
              <button
                onClick={onApprove}
                className="p-1.5 text-white bg-green-600 hover:bg-green-700 rounded transition-colors"
                title="Approve"
              >
                <CheckCircle2 className="w-4 h-4" />
              </button>
              <button
                onClick={() => setShowRejectModal(true)}
                className="p-1.5 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded transition-colors"
                title="Reject"
              >
                <XCircle className="w-4 h-4" />
              </button>
              <button
                onClick={onRegenerate}
                disabled={isRegenerating}
                className="p-1.5 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded transition-colors disabled:opacity-50"
                title="Regenerate"
              >
                <Zap className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </motion.div>

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-lg p-5 max-w-md w-full mx-4 shadow-xl"
          >
            <h3 className="text-lg font-semibold mb-2">Reject E&M Code {emCode.code}</h3>
            <p className="text-sm text-gray-600 mb-3">
              Please provide a reason for rejection:
            </p>
            <textarea
              value={rejectNotes}
              onChange={(e) => setRejectNotes(e.target.value)}
              placeholder="Enter rejection reason..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mb-4 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              rows={3}
              autoFocus
            />
            <div className="flex gap-2">
              <button
                onClick={handleRejectSubmit}
                disabled={!rejectNotes.trim()}
                className="flex-1 px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Confirm Rejection
              </button>
              <button
                onClick={() => {
                  setShowRejectModal(false);
                  setRejectNotes('');
                }}
                className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg"
              >
                Cancel
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </>
  );
};
